import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  UserIcon, 
  ArrowRightIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  GiftIcon,
  StarIcon,
  HeartIcon,
  RocketLaunchIcon,
  EnvelopeIcon,
  PhoneIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { resolveAuthError } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { CenteredLoaderSkeleton } from '../ui/PageSkeleton';
import { extractUsPhoneDigits, formatUsPhoneInput } from '../../utils/usPhone';
import PublicLanguageSwitcher from '../ui/PublicLanguageSwitcher';
import UserAvatar from '../ui/UserAvatar';
import TermsAgreementCheckbox from '../ui/TermsAgreementCheckbox';
import Button from '../ui/Button';
import CelebrationConfetti from '../ui/CelebrationConfetti';
import BirthDatePicker from '../ui/BirthDatePicker';
import ViewportScenes, { ViewportScene } from '../ui/ViewportScenes';

const REFERRAL_LOGO_CLASS = 'fixed top-3 right-3 z-[100] h-7 w-auto object-contain sm:top-4 sm:right-4 sm:h-8';

function ReferralPageChrome({ children, showConfetti = false, fitViewport = false, bare = false }) {
  const { t } = useLanguage();
  const logoAlt = t.brand?.logoAlt || 'Enrola by Elantar';

  return (
    <div
      className={`app-shell gradient-bg relative overflow-hidden ${
        fitViewport ? 'h-[100dvh] max-h-[100dvh]' : 'min-h-screen'
      }`}
    >
      <PublicLanguageSwitcher variant="fixed" fixedAlign="left" compact />
      <img src="/logo.png" alt={logoAlt} className={REFERRAL_LOGO_CLASS} />
      {showConfetti && <CelebrationConfetti />}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse-slow" />
      </div>
      <div
        className={`relative z-10 flex min-h-0 w-full flex-col items-center px-3 sm:px-6 lg:px-8 ${
          bare
            ? 'h-full overflow-y-auto overscroll-contain'
            : fitViewport
              ? 'h-full overflow-y-auto max-lg:py-4 max-lg:pt-14 lg:justify-center lg:overflow-hidden lg:py-12'
              : 'justify-center py-16 sm:py-20'
        }`}
      >
        <div
          className={
            bare
              ? 'my-auto flex w-full flex-col py-6'
              : fitViewport
                ? 'my-auto flex w-full flex-col items-center justify-center max-lg:min-h-[calc(100dvh-3.5rem)] lg:min-h-0'
                : 'w-full'
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function ReferralInviteHeader({
  referrer,
  referrerInfo,
  title,
  subtitle,
  showInvitedBy = true,
  compact = false,
  showAvatar = true,
}) {
  const { t, formatMessage } = useLanguage();
  const displayName = referrer || referrerInfo?.username || '';

  if (compact && !showAvatar) {
    return (
      <div className="mb-3 border-b border-secondary-200/60 pb-3 text-center  max-lg:mb-2.5 max-lg:pb-2.5 lg:mb-5 lg:pb-5">
        <h1 className="text-xl font-bold leading-tight text-orange-600  max-lg:text-xl lg:text-3xl">
          {title || t.referral.createYourAccount}
        </h1>
        {subtitle ? (
          <p className="mt-1 text-sm leading-snug text-secondary-600  max-lg:text-xs max-lg:leading-snug lg:mt-1.5 lg:text-lg">
            {subtitle}
          </p>
        ) : null}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="mb-4 flex items-center gap-3 border-b border-secondary-200/60 pb-4  max-lg:mb-3 max-lg:gap-2.5 max-lg:pb-3 lg:mb-6 lg:gap-5 lg:pb-6">
        {showAvatar ? (
          <UserAvatar
            src={referrerInfo?.avatar_url}
            alt={displayName}
            size="lg"
            variant="brand"
            className="max-lg:!h-14 max-lg:!w-14 max-lg:!text-base shrink-0 ring-4 ring-orange-100  lg:!h-16 lg:!w-16"
          />
        ) : null}
        <div className="min-w-0 text-left">
          <h1 className="text-xl font-bold leading-tight text-orange-600  max-lg:text-xl lg:text-3xl">
            {title || t.referral.welcome}
          </h1>
          {showInvitedBy && displayName ? (
            <p className="mt-1 text-sm leading-snug text-secondary-700  max-lg:text-xs lg:mt-1.5 lg:text-lg">
              {formatMessage(t.referral.invitedByLine, { name: displayName })}
            </p>
          ) : null}
          <p className="mt-0.5 text-xs leading-snug text-secondary-500  max-lg:text-[11px] lg:mt-1 lg:text-base">
            {subtitle || t.referral.joinProgramToday}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-secondary-200/60 pb-8 mb-8 text-center ">
      <UserAvatar
        src={referrerInfo?.avatar_url}
        alt={displayName}
        size="xl"
        variant="brand"
        className="mx-auto mb-5 ring-4 ring-orange-100 shadow-glow "
      />
      <h1 className="text-3xl sm:text-4xl font-bold text-orange-600 ">
        {title || t.referral.welcome}
      </h1>
      {showInvitedBy && displayName ? (
        <p className="mt-3 text-lg text-secondary-700 ">
          {formatMessage(t.referral.invitedByLine, { name: displayName })}
        </p>
      ) : null}
      <p className="mt-2 text-secondary-500 ">
        {subtitle || t.referral.joinProgramToday}
      </p>
    </div>
  );
}

function ReferralPage() {
  const { linkCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const { t, formatMessage } = useLanguage();
  const logoAlt = t.brand?.logoAlt || 'Enrola by Elantar';
  const [loading, setLoading] = useState(true);
  const [referrer, setReferrer] = useState('');
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [programType, setProgramType] = useState('clients');
  const sceneApiRef = useRef(null);
  const [activeSceneIndex, setActiveSceneIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: ''
  });

  const trackReferralClick = useCallback(async () => {
    try {
      const response = await axios.get(`/api/referral/${linkCode}`);
      if (response.data?.valid === false) {
        toast.error(t.referral.invalidReferralLink);
        return;
      }
      const info = response.data.referrer_info;
      setReferrerInfo(info || null);
      setReferrer(info?.username || response.data.referrer || '');
      setProgramType(response.data?.program_type || 'clients');
      
      // Store the referral link code in localStorage for later use during registration
      localStorage.setItem('referralLinkCode', linkCode);
    } catch (error) {
      toast.error(t.referral.invalidReferralLink);
    } finally {
      setLoading(false);
    }
  }, [linkCode, t.referral.invalidReferralLink]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'phone' ? formatUsPhoneInput(value) : value,
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const partnerFlow = programType === 'partners';

    if (!partnerFlow) {
      // Validate password match
      if (formData.password !== formData.confirmPassword) {
        toast.error(t.referral.passwordsDoNotMatch);
        return;
      }

      // Validate password length
      if (formData.password.length < 6) {
        toast.error(t.referral.passwordTooShort);
        return;
      }
    }

    if (extractUsPhoneDigits(formData.phone).length !== 10) {
      toast.error(t.referral.phoneInvalid);
      return;
    }

    if (!partnerFlow && !formData.dateOfBirth) {
      toast.error(t.referral.dateOfBirthRequired);
      return;
    }

    if (!acceptedTerms) {
      toast.error(t.legal.mustAccept);
      return;
    }

    setRegistering(true);

    try {
      // Store referral link code before registration
      localStorage.setItem('referralLinkCode', linkCode);
      
      const result = await register(
        formData.username,
        formData.email,
        formData.phone,
        partnerFlow ? '' : formData.password,
        formData.dateOfBirth || null
      );

      if (result.success) {
        setRegistrationComplete(true);
        setShowConfetti(true);
        toast.success(
          partnerFlow ? t.referral.partnerRequestReceived : t.referral.registrationSuccessful
        );
      } else {
        toast.error(resolveAuthError(result, t, formatMessage, t.referral.registrationFailed));
      }
    } catch (error) {
      toast.error(t.referral.errorOccurred);
    } finally {
      setRegistering(false);
    }
  };

  useEffect(() => {
    trackReferralClick();
  }, [linkCode, trackReferralClick]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('register') === '1') {
      setShowRegistrationForm(true);
    }
  }, [location.search]);

  // Pause the "How Enrola works" video when leaving its scene (mobile carousel);
  // resume it when the video scene is active again.
  useEffect(() => {
    const frames = document.querySelectorAll('.referral-video-frame');
    if (!frames.length) return;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    const func = isMobile && activeSceneIndex !== 0 ? 'pauseVideo' : 'playVideo';
    frames.forEach((frame) => {
      try {
        frame.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func, args: [] }),
          '*'
        );
      } catch (error) {
        /* ignore cross-frame errors */
      }
    });
  }, [activeSceneIndex]);

  const handleShowRegistrationForm = () => {
    setShowRegistrationForm(true);
  };

  const handleNavigateToLogin = () => {
    // The referral code is already stored in localStorage from trackReferralClick
    // Navigate to login page
    navigate('/login');
  };

  const isPartner = programType === 'partners';

  const features = isPartner
    ? [
        {
          icon: CurrencyDollarIcon,
          title: t.referral.partnerRewards,
          description: t.referral.partnerRewardsDesc,
        },
        {
          icon: RocketLaunchIcon,
          title: t.referral.partnerGrowth,
          description: t.referral.partnerGrowthDesc,
        },
        {
          icon: BriefcaseIcon,
          title: t.referral.partnerNetwork,
          description: t.referral.partnerNetworkDesc,
        },
      ]
    : [
        {
          icon: GiftIcon,
          title: t.referral.exclusiveRewards,
          description: t.referral.exclusiveRewardsDesc,
        },
        {
          icon: StarIcon,
          title: t.referral.premiumProgram,
          description: t.referral.premiumProgramDesc,
        },
        {
          icon: HeartIcon,
          title: t.referral.activeCommunity,
          description: t.referral.activeCommunityDesc,
        },
      ];

  if (loading) {
    return (
      <>
        <PublicLanguageSwitcher variant="fixed" fixedAlign="left" />
        <img src="/logo.png" alt={logoAlt} className={REFERRAL_LOGO_CLASS} />
        <CenteredLoaderSkeleton />
      </>
    );
  }

  if (registrationComplete) {
    return (
      <ReferralPageChrome showConfetti fitViewport>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto w-full max-w-xl max-lg:max-w-md"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 to-amber-500 p-6 text-center text-white shadow-glow max-lg:p-5 lg:p-10"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/30 max-lg:mb-3 max-lg:h-12 max-lg:w-12 lg:mb-5 lg:h-16 lg:w-16">
              <CheckCircleIcon className="h-8 w-8 text-white max-lg:h-7 max-lg:w-7 lg:h-9 lg:w-9" />
            </div>
            <h2 className="text-xl font-bold max-lg:text-xl lg:text-3xl">
              {isPartner ? t.referral.partnerRequestReceivedTitle : t.referral.accountCreatedSuccess}
            </h2>
            <p className="mt-2 text-sm text-white/90 max-lg:mt-1.5 max-lg:text-sm lg:mt-3 lg:text-lg">
              {isPartner ? t.referral.partnerRequestReceivedMessage : t.referral.registrationSuccessMessage}
            </p>
            {!isPartner && (
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleNavigateToLogin}
                className="mt-5 w-full rounded-xl bg-white py-3 text-sm font-semibold text-orange-600 shadow-soft transition-colors hover:bg-orange-50 max-lg:mt-4 max-lg:py-2.5 lg:mt-8 lg:py-4 lg:text-lg"
              >
                {t.referral.signIn}
              </motion.button>
            )}
          </motion.div>
        </motion.div>
      </ReferralPageChrome>
    );
  }

  // If registration form is shown, display it
  if (showRegistrationForm) {
    return (
      <ReferralPageChrome showConfetti={showConfetti} fitViewport>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto w-full max-w-xl max-lg:max-w-md"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="card-premium shadow-premium p-4 max-lg:p-3.5 lg:p-8"
          >
            <ReferralInviteHeader
              title={t.referral.createYourAccount}
              subtitle={formatMessage(t.referral.referredByLine, { name: referrer })}
              compact
              showAvatar={false}
            />

            <form onSubmit={handleRegister} className="space-y-2.5 max-lg:space-y-2 lg:space-y-4">
              <div className="grid grid-cols-2 gap-2 max-lg:gap-2 lg:gap-4">
                <div>
                  <label className="form-label mb-1 max-lg:mb-0.5 max-lg:text-xs lg:mb-1.5">{t.referral.username}</label>
                  <div className="relative">
                    <UserIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-orange-500 max-lg:h-3.5 max-lg:w-3.5 lg:left-4 lg:h-5 lg:w-5" />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className="input-premium py-2 pl-9 text-sm max-lg:py-2 max-lg:pl-8 max-lg:text-sm lg:py-3 lg:pl-12"
                      placeholder={t.referral.chooseUsername}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label mb-1 max-lg:mb-0.5 max-lg:text-xs lg:mb-1.5">{t.referral.email}</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-orange-500 max-lg:h-3.5 max-lg:w-3.5 lg:left-4 lg:h-5 lg:w-5" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="input-premium py-2 pl-9 text-sm max-lg:py-2 max-lg:pl-8 max-lg:text-sm lg:py-3 lg:pl-12"
                      placeholder={t.referral.emailPlaceholder}
                      required
                    />
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="form-label mb-1 max-lg:mb-0.5 max-lg:text-xs lg:mb-1.5">{t.referral.phone}</label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-orange-500 max-lg:h-3.5 max-lg:w-3.5 lg:left-4 lg:h-5 lg:w-5" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="input-premium py-2 pl-9 text-sm max-lg:py-2 max-lg:pl-8 max-lg:text-sm lg:py-3 lg:pl-12"
                      placeholder={t.referral.phonePlaceholder}
                      inputMode="numeric"
                      autoComplete="tel-national"
                      maxLength={14}
                      required
                    />
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="form-label mb-1 max-lg:mb-0.5 max-lg:text-xs lg:mb-1.5" htmlFor="referral-date-of-birth">
                    {isPartner ? t.referral.dateOfBirthOptional : t.referral.dateOfBirth}
                  </label>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  >
                    <BirthDatePicker
                      id="referral-date-of-birth"
                      value={formData.dateOfBirth}
                      onChange={(dateOfBirth) => setFormData((current) => ({ ...current, dateOfBirth }))}
                      required={!isPartner}
                    />
                  </motion.div>
                </div>

                {!isPartner && (
                  <>
                    <div>
                      <label className="form-label mb-1 max-lg:mb-0.5 max-lg:text-xs lg:mb-1.5">{t.referral.password}</label>
                      <div className="relative">
                        <LockClosedIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-orange-500 max-lg:h-3.5 max-lg:w-3.5 lg:left-4 lg:h-5 lg:w-5" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="input-premium py-2 pl-9 pr-9 text-sm max-lg:py-2 max-lg:pl-8 max-lg:pr-8 max-lg:text-sm lg:py-3 lg:pl-12 lg:pr-12"
                          placeholder={t.referral.passwordMinPlaceholder}
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600 lg:right-4"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-4 w-4 max-lg:h-3.5 max-lg:w-3.5 lg:h-5 lg:w-5" />
                          ) : (
                            <EyeIcon className="h-4 w-4 max-lg:h-3.5 max-lg:w-3.5 lg:h-5 lg:w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="form-label mb-1 max-lg:mb-0.5 max-lg:text-xs lg:mb-1.5">{t.referral.confirmPassword}</label>
                      <div className="relative">
                        <LockClosedIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-orange-500 max-lg:h-3.5 max-lg:w-3.5 lg:left-4 lg:h-5 lg:w-5" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="input-premium py-2 pl-9 text-sm max-lg:py-2 max-lg:pl-8 max-lg:text-sm lg:py-3 lg:pl-12"
                          placeholder={t.referral.confirmPasswordPlaceholder}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <TermsAgreementCheckbox
                checked={acceptedTerms}
                onChange={setAcceptedTerms}
                returnTo={`/referral/${linkCode}?register=1`}
                id="referral-terms-agreement"
                className="pt-1"
              />

              <div className="space-y-2 pt-0.5 max-lg:space-y-2 lg:space-y-3 lg:pt-1">
                <Button
                  type="submit"
                  variant="primary"
                  loading={registering}
                  disabled={registering || !acceptedTerms || (!isPartner && !formData.dateOfBirth)}
                  icon={ArrowRightIcon}
                  iconPosition="right"
                  className="w-full py-2.5 text-sm max-lg:py-2.5 max-lg:text-sm lg:py-4 lg:text-lg"
                >
                  {isPartner ? t.referral.partnerSubmit : t.referral.createAccount}
                </Button>

                <div className="grid grid-cols-2 gap-2 max-lg:gap-2 lg:gap-3">
                  <button
                    type="button"
                    onClick={() => setShowRegistrationForm(false)}
                    className="flex items-center justify-center rounded-xl border border-secondary-200/80 py-2.5 text-sm font-medium text-orange-600 transition-colors hover:bg-orange-50   max-lg:py-2.5 max-lg:text-xs lg:py-4 lg:text-base"
                  >
                    ← {t.referral.back}
                  </button>

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleNavigateToLogin}
                    icon={CheckCircleIcon}
                    className="py-2.5 text-sm max-lg:py-2.5 max-lg:text-xs lg:py-4 lg:text-base"
                  >
                    {t.referral.signIn}
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      </ReferralPageChrome>
    );
  }

  const videoUrl = `https://www.youtube.com/embed/${isPartner ? 'RBfFnZCnKu8' : 'MoNo0yGvX0A'}?autoplay=1&controls=1&rel=0&modestbranding=1&playsinline=1&showinfo=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;

  const videoCard = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.05 }}
      className="relative w-full overflow-hidden rounded-3xl bg-black ring-1 ring-black/5"
    >
      <div className="aspect-video w-full">
        <iframe
          src={videoUrl}
          title={
            isPartner
              ? (t.referral.partnerVideoTitle || 'Partner program video')
              : (t.referral.programVideoTitle || 'Program video')
          }
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="referral-video-frame absolute inset-0 h-full w-full"
          loading="lazy"
        />
      </div>
    </motion.div>
  );

  const welcomeCard = (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="card-premium shadow-premium p-4 w-full max-lg:max-w-md lg:p-8"
    >
      <ReferralInviteHeader
        referrer={referrer}
        referrerInfo={referrerInfo}
        compact
        subtitle={isPartner ? t.referral.joinPartnerProgramToday : undefined}
      />

      <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-orange-600  max-lg:mb-2.5 max-lg:text-[11px] lg:mb-5 lg:text-base">
        {isPartner ? t.referral.partnerWhatAwaitsYou : t.referral.whatAwaitsYou}
      </p>

      <div className="mb-4 grid grid-cols-3 gap-2 max-lg:mb-3 max-lg:gap-2 lg:mb-6 lg:gap-4">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15 + index * 0.05 }}
            className="rounded-xl border border-orange-100/80 bg-orange-50/50 px-2 py-2.5 text-center   max-lg:px-1.5 max-lg:py-2 lg:px-3 lg:py-4"
          >
            <div className="mx-auto mb-1.5 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 max-lg:mb-1 max-lg:h-8 max-lg:w-8 lg:mb-2 lg:h-11 lg:w-11">
              <feature.icon className="h-4 w-4 text-white max-lg:h-3.5 max-lg:w-3.5 lg:h-6 lg:w-6" />
            </div>
            <p className="text-[10px] font-semibold leading-tight text-secondary-900  max-lg:text-[10px] lg:text-sm">
              {feature.title}
            </p>
          </motion.div>
        ))}
      </div>

      <div className="space-y-2 max-lg:space-y-2 lg:space-y-3">
        <Button
          variant="primary"
          onClick={handleShowRegistrationForm}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.35 }}
          className="w-full gap-2 py-2.5 text-sm max-lg:py-2.5 max-lg:text-sm lg:py-4 lg:text-lg"
        >
          <UserIcon className="h-4 w-4 max-lg:h-4 max-lg:w-4 lg:h-5 lg:w-5" aria-hidden="true" />
          {t.referral.createAccount}
          <ArrowRightIcon className="h-4 w-4 max-lg:h-4 max-lg:w-4 lg:h-5 lg:w-5" aria-hidden="true" />
        </Button>

        <div className="grid grid-cols-2 gap-2 max-lg:gap-2 lg:gap-3">
          <Button
            variant="secondary"
            onClick={handleNavigateToLogin}
            icon={CheckCircleIcon}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.4 }}
            className="py-2.5 text-sm max-lg:py-2.5 max-lg:text-xs lg:py-4 lg:text-base"
          >
            {t.referral.signIn}
          </Button>

          <motion.a
            href="/"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.45 }}
            className="flex items-center justify-center rounded-xl border border-secondary-200/80 py-2.5 text-sm font-medium text-orange-600 transition-colors hover:bg-orange-50   max-lg:py-2.5 max-lg:text-xs lg:py-4 lg:text-base"
          >
            {t.referral.goToHome}
          </motion.a>
        </div>
      </div>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-secondary-400 max-lg:mt-2 max-lg:text-[10px] lg:mt-5 lg:gap-2 lg:text-sm">
        <RocketLaunchIcon className="h-3.5 w-3.5 shrink-0 max-lg:h-3 max-lg:w-3 lg:h-4 lg:w-4" />
        <span>{t.referral.referralProgram}</span>
      </p>
    </motion.div>
  );

  return (
    <ReferralPageChrome showConfetti={showConfetti} fitViewport bare>
      {/* Mobile — same scene format as the landing (snap: video → welcome) */}
      <ViewportScenes sceneApiRef={sceneApiRef} className="lg:hidden">
        <ViewportScene
          id="referral-video"
          innerClassName="items-center justify-center px-4 py-4"
        >
          {videoCard}

          {/* "Continue with registration" hint — gray text + gently bouncing down arrow */}
          <button
            type="button"
            onClick={() => sceneApiRef.current?.goToIndex(1)}
            className="pointer-events-auto absolute bottom-8 left-0 right-0 z-20 flex flex-col items-center gap-1 px-4 text-center"
          >
            <motion.span
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: [0.37, 0, 0.63, 1] }}
              className="text-sm font-medium text-secondary-500"
            >
              {t.referral.continueWithRegistration}
            </motion.span>
            <motion.span
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: [0.37, 0, 0.63, 1] }}
              className="text-secondary-500"
            >
              <ArrowDownIcon className="h-5 w-5" aria-hidden="true" />
            </motion.span>
          </button>
        </ViewportScene>
        <ViewportScene
          id="referral-welcome"
          innerClassName="items-center justify-center px-4 py-4"
        >
          {welcomeCard}
        </ViewportScene>
      </ViewportScenes>

      {/* Desktop — two-column layout: welcome card (left) + video (right), centered */}
      <div className="mx-auto hidden w-full max-w-6xl lg:grid lg:grid-cols-[minmax(0,4fr)_minmax(0,6fr)] lg:items-center lg:gap-10">
        {welcomeCard}
        {videoCard}
      </div>
    </ReferralPageChrome>
  );
}

export default ReferralPage;